import { PanelBase } from "./PanelBase";
import type { GameContext } from "../../GameContext";

interface AuctionListing {
  listingId: string;
  itemName: string;
  price: number;
  sellerName: string;
}

interface MailItem {
  mailId: string;
  from: string;
  subject: string;
  hasAttachment: boolean;
  claimed: boolean;
}

type Tab = "auction" | "mail";

/**
 * Per M07 (economy-worker): currency, trade, auction (list/bid/buyout),
 * mail (send/claim), gatewayed at /api/v1/trade*, /api/v1/auction*,
 * /api/v1/mail*, /api/v1/characters/:id/currency (2.2). Trade (live
 * player-to-player) needs its own realtime negotiation UI beyond this
 * panel's scope -- this delivery covers the auction house and mail tabs,
 * which are both plain request/response flows.
 */
export class EconomyPanel extends PanelBase {
  private activeTab: Tab = "auction";

  constructor(root: HTMLElement, ctx: GameContext, private readonly characterId: string) {
    super(root, ctx, "economy-panel");
  }

  protected async refresh(): Promise<void> {
    this.el.innerHTML = "<h3>Trade</h3>";

    const tabBar = document.createElement("div");
    tabBar.className = "economy-tab-bar";
    for (const tab of ["auction", "mail"] as Tab[]) {
      const btn = document.createElement("button");
      btn.textContent = tab === "auction" ? "Auction House" : "Mail";
      btn.classList.toggle("active", tab === this.activeTab);
      btn.addEventListener("click", () => {
        this.activeTab = tab;
        void this.refresh();
      });
      tabBar.appendChild(btn);
    }
    this.el.appendChild(tabBar);

    const contentEl = document.createElement("div");
    contentEl.className = "economy-tab-content";
    this.el.appendChild(contentEl);

    if (this.activeTab === "auction") {
      await this.renderAuction(contentEl);
    } else {
      await this.renderMail(contentEl);
    }
  }

  private async renderAuction(container: HTMLElement): Promise<void> {
    container.innerHTML = "<p>Loading listings…</p>";
    const result = await this.ctx.network.rest.get<AuctionListing[]>("/api/v1/auction");
    if (!result.ok) {
      container.innerHTML = `<p class="panel-error">${escapeHtml(result.message)}</p>`;
      return;
    }

    container.innerHTML = "";
    for (const listing of result.data) {
      const row = document.createElement("div");
      row.className = "auction-row";
      row.innerHTML = `
        <span>${escapeHtml(listing.itemName)}</span>
        <span>${listing.price}g</span>
        <span>${escapeHtml(listing.sellerName)}</span>
      `;
      const buyBtn = document.createElement("button");
      buyBtn.textContent = "Buyout";
      buyBtn.addEventListener("click", () => void this.buyout(listing.listingId));
      row.appendChild(buyBtn);
      container.appendChild(row);
    }
  }

  private async buyout(listingId: string): Promise<void> {
    // Idempotency-Key required (2.7: any endpoint moving currency/items).
    const result = await this.ctx.network.rest.post(
      `/api/v1/auction/${listingId}/buyout`,
      {},
      { idempotencyKey: `buyout-${listingId}-${this.characterId}` }
    );
    if (result.ok) await this.refresh();
  }

  private async renderMail(container: HTMLElement): Promise<void> {
    container.innerHTML = "<p>Loading mail…</p>";
    const result = await this.ctx.network.rest.get<MailItem[]>("/api/v1/mail");
    if (!result.ok) {
      container.innerHTML = `<p class="panel-error">${escapeHtml(result.message)}</p>`;
      return;
    }

    container.innerHTML = "";
    if (result.data.length === 0) {
      container.innerHTML = "<p>No mail.</p>";
      return;
    }

    for (const mail of result.data) {
      const row = document.createElement("div");
      row.className = "mail-row";
      row.innerHTML = `
        <span>${escapeHtml(mail.from)}</span>
        <span>${escapeHtml(mail.subject)}</span>
      `;
      if (mail.hasAttachment && !mail.claimed) {
        const claimBtn = document.createElement("button");
        claimBtn.textContent = "Claim";
        claimBtn.addEventListener("click", () => void this.claimMail(mail.mailId));
        row.appendChild(claimBtn);
      }
      container.appendChild(row);
    }
  }

  private async claimMail(mailId: string): Promise<void> {
    const result = await this.ctx.network.rest.post(
      `/api/v1/mail/${mailId}/claim`,
      {},
      { idempotencyKey: `mail-claim-${mailId}` }
    );
    if (result.ok) await this.refresh();
  }
}

function escapeHtml(value: string): string {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
}
